import React, { useState } from 'react';
import { Grid, Tab, Tabs, Typography } from '@material-ui/core';
import { GetApp, CheckCircle } from '@material-ui/icons';
import { interoperabilidadeStatusDescriptions, tipoInteroperabilidadeDescriptions } from 'common/genericDescriptions';
import { OperacaoInteroperabilidadeResponse, Perfil } from 'common/genericTypes';
import { GenericDrawer } from 'components/genericDrawer';
import { HorizontalAppBar } from 'components/horizontalAppBar';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LgSpace, MdSpace, SmSpace, Section, Button, TextField } from 'm2m.components';
import { formatDate } from 'utils/date';
import { formatNumber, formatEightDecimalsCurrency } from 'utils/number';
import { RootState } from 'store';
import { useGenericStyles } from 'common/genericStyles';
import { addAlert } from 'store/modules/snackBar';

const DetalhesInteroperabilidade: React.FC<RouteComponentProps<{}, {}, OperacaoInteroperabilidadeResponse>> = (
  props
) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useGenericStyles();

  const {
    getInteroperabilidadeDocumentDownloadUrl,
    putAprovarTransferenciaInteroperabilidade,
    putRecusarTransferenciaInteroperabilidade,
  } = useSelector((state: RootState) => state.operacaoApi);
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const operacao = props.location.state;

  const [tabValue, setTabValue] = useState<number>(0);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [isConfirmacao, setIsConfirmacao] = useState<boolean>(false);
  const [justificativa, setJustificativa] = useState<string>('');

  const [sucesso, setSucesso] = useState<boolean>(false);

  const getAttachmentLink = async (interoperabilidadeId: string, documentId: string, name: string) => {
    try {
      const result = await getInteroperabilidadeDocumentDownloadUrl(dispatch, interoperabilidadeId, documentId, true);

      if (result.data) {
        const link = document.createElement('a');
        link.href = result.data;
        link.target = '_blank';
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch {}
  };

  const confirmarTransferencia = async () => {
    setOpenDrawer(false);
    try {
      await putAprovarTransferenciaInteroperabilidade(dispatch, operacao.interoperabilidade.id, true);
      setSucesso(true);
    } catch {}
  };

  const recusarTransferencia = async () => {
    if (!justificativa) {
      dispatch(
        addAlert({
          severity: 'error',
          message: 'É obrigatório o preenchimento da justificativa para recusar essa movimentação.',
        })
      );
      return;
    }

    try {
      setOpenDrawer(false);
      await putRecusarTransferenciaInteroperabilidade(dispatch, operacao.interoperabilidade.id, justificativa, true);
      setJustificativa('');
      setSucesso(true);
    } catch {}
  };

  const handleOpenDrawer = (confirmacao: boolean) => {
    setIsConfirmacao(confirmacao);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setJustificativa('');
  };

  const goBack = () => history.push('/operacoes/gerenciar', { initialTab: 2 });

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Interoperabilidade'}</Typography>
      <SmSpace />

      <Button onClick={goBack} variant="contained" color="primary">
        {'Voltar para a página anterior'}
      </Button>

      <LgSpace />
      {sucesso ? (
        <Grid container direction="column" justify="center" alignItems="center">
          <div className={classes.iconWrapper}>
            <CheckCircle style={{ width: '40px', height: '40px' }} color="secondary" />
          </div>
          <SmSpace />
          <Typography variant="h3">{`Transferência de interoperabilidade ${
            isConfirmacao ? 'concluída' : 'recusada'
          } com sucesso`}</Typography>
          <MdSpace />
          <Button onClick={goBack} variant="contained" color="primary">
            {'Voltar para a página inicial'}
          </Button>
          <MdSpace />
        </Grid>
      ) : (
        <>
          <HorizontalAppBar>
            <Tabs indicatorColor="primary" value={tabValue} onChange={(_event, newTab) => setTabValue(newTab)}>
              <Tab label="Detalhes da Movimentação" />
            </Tabs>
          </HorizontalAppBar>
          {tabValue === 0 && (
            <>
              <Section title={'Detalhes da Movimentação'} defaultGrid>
                <div>
                  <Typography variant="body2">{'Tipo:'}</Typography>
                  <Typography variant="body1">
                    {tipoInteroperabilidadeDescriptions[operacao.interoperabilidade.tipo]}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Status:'}</Typography>
                  <Typography variant="body1">
                    {interoperabilidadeStatusDescriptions[operacao.interoperabilidade.statusAprovacao ?? '']}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Código do Instrumento:'}</Typography>
                  <Typography variant="body1">{operacao.instrumentoFinanceiro.codigo}</Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Data da Solicitação:'}</Typography>
                  <Typography variant="body1">{formatDate(operacao.dataDeCriacao)}</Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Custodiante:'}</Typography>
                  <Typography variant="body1">{operacao.custodiante.razaoSocial}</Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Investidor:'}</Typography>
                  <Typography variant="body1">{operacao.interoperabilidade.investidor.razaoSocial}</Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Quantidade alocada:'}</Typography>
                  <Typography variant="body1">{formatNumber(operacao.interoperabilidade.quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}</Typography>
                </div>
                <div>
                  <Typography variant="body2">{'PU'}</Typography>
                  <Typography variant="body1">{formatEightDecimalsCurrency(operacao.interoperabilidade.precoUnitario)}</Typography>
                </div>
                <div>
                  <Typography variant="body2">{'Justificativa'}</Typography>
                  <Typography variant="body1">{operacao.interoperabilidade.justificativa}</Typography>
                </div>
              </Section>
              {operacao.interoperabilidade.documentos.length > 0 && (
                <Section title={'Documentos'} defaultGrid>
                  {operacao.interoperabilidade.documentos.map((doc) => (
                    <Button
                      key={doc.documentId}
                      style={{ height: 'fit-content' }}
                      endIcon={<GetApp />}
                      onClick={() => getAttachmentLink(doc.interoperabilidadeId, doc.documentId, doc.fileName)}
                      variant="text"
                    >
                      {`${doc.fileName} - ${Math.round((doc.size / 1000000) * 100) / 100}MB`}
                    </Button>
                  ))}
                </Section>
              )}
            </>
          )}
          <LgSpace />
          <Grid container justify="center" spacing={4} style={{ width: '100%' }}>
            {!isUsuarioLeitura && (
              <>
                <Grid item md={2}>
                  <Button onClick={() => handleOpenDrawer(false)} variant="contained" color="error">
                    {'Recusar Movimentação'}
                  </Button>
                </Grid>
                <Grid item md={2}>
                  <Button onClick={() => handleOpenDrawer(true)} variant="contained" color="primary">
                    {'Comandar Movimentação'}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
          <LgSpace />
          <GenericDrawer
            opened={openDrawer}
            onClose={() => handleCloseDrawer()}
            title={`${isConfirmacao ? 'Confirmar' : 'Recusar'} Transferência`}
            textContents={[
              {
                text: `Tem certeza que deseja ${
                  isConfirmacao ? 'confirmar' : 'recusar'
                } essa transferência por interoperabilidade?`,
              },
            ]}
            buttons={[
              isConfirmacao
                ? {
                    buttonText: `Confirmar`,
                    buttonAction: confirmarTransferencia,
                    buttonColor: 'primary',
                  }
                : {
                    buttonText: `Recusar`,
                    buttonAction: recusarTransferencia,
                    buttonColor: 'error',
                  },
              {
                buttonText: 'Cancelar',
                buttonAction: handleCloseDrawer,
              },
            ]}
          >
            {!isConfirmacao && (
              <>
                <SmSpace />
                <TextField
                  label="Justificativa *"
                  multiline
                  rows={4}
                  value={justificativa}
                  onChange={setJustificativa}
                />
                <MdSpace />
              </>
            )}
          </GenericDrawer>
        </>
      )}
    </div>
  );
};

export default DetalhesInteroperabilidade;
